import axios from "axios";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { aiConfidenceService } from "@src/pages/AiConfidence/aiConfidenceService";
import {
	AI_CONFIDENCE_SEVERITY,
	AiConfidenceHealth,
	AiConfidenceRow,
	AiConfidenceSeverity,
	AiConfidenceSortBy,
	AiConfidenceSummary,
	AiConfidenceTriageState,
	ListAiConfidenceParams,
	BulkAiConfidenceParams,
} from "@src/pages/AiConfidence/types";

/* The backend puts the reason in the body; axios only gives "Request failed with status code 500". */
const messageFrom = (error: unknown, fallback: string): string => {
	if (axios.isAxiosError(error)) {
		return (error.response?.data as { message?: string } | undefined)?.message || error.message || fallback;
	}

	return error instanceof Error ? error.message : fallback;
};

type LoadingState = "idle" | "pending" | "succeeded" | "failed";

interface AiConfidenceState {
	rows: Array<AiConfidenceRow>;
	total: number;
	summary: AiConfidenceSummary;
	ticketsOnFlaggedMappings: number;
	health: AiConfidenceHealth | null;
	currentPage: number;
	itemsPerPage: number;
	severities: Array<AiConfidenceSeverity>;
	marketplaceId: number | null;
	triageState: AiConfidenceTriageState;
	searchTerm: string;
	sortBy: AiConfidenceSortBy;
	listLoading: LoadingState;
	latestQueueRequestId: string | null;
	mutationLoading: LoadingState;
	pendingRowId: number | null;
	error: string | null;
}

export const DEFAULT_SEVERITIES: Array<AiConfidenceSeverity> = [
	AI_CONFIDENCE_SEVERITY.CRITICAL,
	AI_CONFIDENCE_SEVERITY.HIGH,
	AI_CONFIDENCE_SEVERITY.MEDIUM,
];

const initialState: AiConfidenceState = {
	rows: [],
	total: 0,
	summary: { criticalCount: 0, highCount: 0, mediumCount: 0, lowCount: 0, okCount: 0, checkErrorCount: 0, lastCheckedOn: null },
	ticketsOnFlaggedMappings: 0,
	health: null,
	currentPage: 1,
	itemsPerPage: 30,
	severities: DEFAULT_SEVERITIES,
	marketplaceId: null,
	triageState: "open",
	searchTerm: "",
	sortBy: "urgency",
	listLoading: "idle",
	latestQueueRequestId: null,
	mutationLoading: "idle",
	pendingRowId: null,
	error: null,
};

export const fetchAiConfidenceQueue = createAsyncThunk(
	"aiConfidence/fetchQueue",
	async (params: ListAiConfidenceParams, { rejectWithValue }) => {
		try {
			return await aiConfidenceService.listQueue(params);
		} catch (error) {
			return rejectWithValue(messageFrom(error, "Failed to load the AI confidence queue."));
		}
	},
);

export const fetchAiConfidenceHealth = createAsyncThunk("aiConfidence/fetchHealth", async (_, { rejectWithValue }) => {
	try {
		return await aiConfidenceService.getHealth();
	} catch (error) {
		return rejectWithValue(messageFrom(error, "Failed to read pipeline health."));
	}
});

export const acknowledgeAiConfidenceFlag = createAsyncThunk(
	"aiConfidence/acknowledge",
	async ({ mappingEventId, reason }: { mappingEventId: number; reason: string }, { rejectWithValue }) => {
		try {
			await aiConfidenceService.acknowledge(mappingEventId, reason);
			toast.success("Mapping acknowledged");
			return mappingEventId;
		} catch (error) {
			return rejectWithValue(messageFrom(error, "Failed to acknowledge the mapping."));
		}
	},
);

export const recheckAiConfidence = createAsyncThunk(
	"aiConfidence/recheck",
	async ({ mappingEventId }: { mappingEventId: number }, { rejectWithValue }) => {
		try {
			await aiConfidenceService.recheck(mappingEventId);
			toast.success("Re-running confidence check", { description: "1 mapping(s) queued." });
			return mappingEventId;
		} catch (error) {
			return rejectWithValue(messageFrom(error, "Failed to queue the re-check."));
		}
	},
);

export const bulkAiConfidenceAction = createAsyncThunk(
	"aiConfidence/bulk",
	async (params: BulkAiConfidenceParams, { rejectWithValue }) => {
		try {
			const result = await aiConfidenceService.bulk(params);
			const verb = params.action === "acknowledge" ? "acknowledged" : "queued for re-check";

			if (result.failed > 0) {
				toast.warning(`${result.applied} of ${result.matched} ${verb}`, { description: `${result.failed} could not be processed.` });
			} else {
				toast.success(`${result.applied} mapping(s) ${verb}`);
			}

			return result;
		} catch (error) {
			return rejectWithValue(messageFrom(error, "The bulk action could not be applied."));
		}
	},
);

export const aiConfidenceSlice = createSlice({
	name: "aiConfidence",
	initialState,
	reducers: {
		setCurrentPage(state, action: PayloadAction<number>) {
			state.currentPage = action.payload;
		},
		setItemsPerPage(state, action: PayloadAction<number>) {
			state.itemsPerPage = action.payload;
			state.currentPage = 1;
		},
		setSeverities(state, action: PayloadAction<Array<AiConfidenceSeverity>>) {
			state.severities = action.payload;
			state.currentPage = 1;
		},
		setMarketplaceId(state, action: PayloadAction<number | null>) {
			state.marketplaceId = action.payload;
			state.currentPage = 1;
		},
		setTriageState(state, action: PayloadAction<AiConfidenceTriageState>) {
			state.triageState = action.payload;
			state.currentPage = 1;
		},
		setSearchTerm(state, action: PayloadAction<string>) {
			state.searchTerm = action.payload;
			state.currentPage = 1;
		},
		setSortBy(state, action: PayloadAction<AiConfidenceSortBy>) {
			state.sortBy = action.payload;
			state.currentPage = 1;
		},
		setPendingRowId(state, action: PayloadAction<number | null>) {
			state.pendingRowId = action.payload;
		},
		clearRows(state) {
			state.rows = [];
			state.total = 0;
			state.ticketsOnFlaggedMappings = 0;
			state.listLoading = "succeeded";
		},
		dropRow(state, action: PayloadAction<number>) {
			state.rows = state.rows.filter((row) => row.mappingEventId !== action.payload);
			state.total = Math.max(0, state.total - 1);
			state.currentPage = Math.min(state.currentPage, Math.max(1, Math.ceil(state.total / state.itemsPerPage)));
		},
		resetFilters(state) {
			state.severities = DEFAULT_SEVERITIES;
			state.marketplaceId = null;
			state.triageState = "open";
			state.searchTerm = "";
			state.sortBy = "urgency";
			state.currentPage = 1;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchAiConfidenceQueue.pending, (state, action) => {
				state.listLoading = "pending";
				state.latestQueueRequestId = action.meta.requestId;
				state.error = null;
			})
			.addCase(fetchAiConfidenceQueue.fulfilled, (state, action) => {
				if (state.latestQueueRequestId !== action.meta.requestId) return;

				state.listLoading = "succeeded";
				state.rows = action.payload.items;
				state.total = action.payload.total;
				state.summary = action.payload.summary;
				state.ticketsOnFlaggedMappings = action.payload.ticketsOnFlaggedMappings;
			})
			.addCase(fetchAiConfidenceQueue.rejected, (state, action) => {
				if (state.latestQueueRequestId !== action.meta.requestId) return;

				state.listLoading = "failed";
				state.error = action.payload as string;
				toast.error(action.payload as string);
			})
			.addCase(fetchAiConfidenceHealth.fulfilled, (state, action) => {
				state.health = action.payload;
			})
			.addCase(fetchAiConfidenceHealth.rejected, (state) => {
				state.health = null;
			})
			.addCase(acknowledgeAiConfidenceFlag.pending, (state, action) => {
				state.mutationLoading = "pending";
				state.pendingRowId = action.meta.arg.mappingEventId;
			})
			.addCase(acknowledgeAiConfidenceFlag.fulfilled, (state, action) => {
				state.mutationLoading = "succeeded";
				if (state.triageState === "open") {
					state.rows = state.rows.filter((row) => row.mappingEventId !== action.payload);
					state.total = Math.max(0, state.total - 1);
					state.currentPage = Math.min(state.currentPage, Math.max(1, Math.ceil(state.total / state.itemsPerPage)));
				}
				state.pendingRowId = null;
			})
			.addCase(acknowledgeAiConfidenceFlag.rejected, (state, action) => {
				state.mutationLoading = "failed";
				state.error = action.payload as string;
				toast.error(action.payload as string);
				state.pendingRowId = null;
			})
			.addCase(recheckAiConfidence.pending, (state, action) => {
				state.mutationLoading = "pending";
				state.pendingRowId = action.meta.arg.mappingEventId;
			})
			.addCase(recheckAiConfidence.fulfilled, (state) => {
				state.mutationLoading = "succeeded";
				state.pendingRowId = null;
			})
			.addCase(recheckAiConfidence.rejected, (state, action) => {
				state.mutationLoading = "failed";
				state.error = action.payload as string;
				toast.error(action.payload as string);
				state.pendingRowId = null;
			})
			.addCase(bulkAiConfidenceAction.pending, (state) => {
				state.mutationLoading = "pending";
			})
			.addCase(bulkAiConfidenceAction.fulfilled, (state) => {
				state.mutationLoading = "succeeded";
			})
			.addCase(bulkAiConfidenceAction.rejected, (state, action) => {
				state.mutationLoading = "failed";
				state.error = action.payload as string;
				toast.error(action.payload as string);
			});
	},
});

export const {
	setCurrentPage,
	setItemsPerPage,
	setSeverities,
	setMarketplaceId,
	setTriageState,
	setSearchTerm,
	setSortBy,
	clearRows,
	dropRow,
	setPendingRowId,
	resetFilters,
} = aiConfidenceSlice.actions;

export default aiConfidenceSlice.reducer;
